import React from 'react';

import { LabelInput, TimePicker, Checkbox } from '@/components/ui/inputs';
import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton';
import { PermissionGuard, SidePanelFormLayout } from '@/components/layout';
import { useTranslation } from '@/i18n';
import { useEditFractionalLeaveForm } from '@/hooks/fractionalLeave';
import type { FractionalLeave } from '@/api/domains/fractionalLeave/fractionalLeave.types';


// ── Props ──────────────────────────────────────────────────────────────────

interface FractionalLeaveFormPanelProps {
  record?: FractionalLeave;
  readOnly?: boolean;
  onSubmit?: () => void;
}

// ── Component ──────────────────────────────────────────────────────────────

export const FractionalLeaveFormPanel: React.FC<FractionalLeaveFormPanelProps> = ({
  record,
  readOnly,
  onSubmit,
}) => {
  const { t: translate } = useTranslation();

  const {
    nameValue,
    descriptionValue,
    hoursValue,
    deductibleValue,
    handleNameChange,
    handleDescriptionChange,
    handleHoursChange,
    handleDeductibleChange,
    handleSubmit,
    effectiveReadOnly,
    isSubmitting,
    isReady,
    errors,
    isSubmitted,
  } = useEditFractionalLeaveForm({ record, readOnly, onSubmit });

  return (
    <SidePanelFormLayout onSubmit={handleSubmit} data-testid="fractional-leave-form-panel">
      <SidePanelFormLayout.Fields>
        <LabelInput
          label={translate('fractionalLeave:form.name')}
          value={nameValue}
          onChange={handleNameChange}
          required
          disabled={effectiveReadOnly || isSubmitting}
          color={errors.name && isSubmitted ? 'error' : 'default'}
          supportingText={
          errors.name?.message && isSubmitted
            ? translate(String(errors.name.message))
            : undefined
        }
          data-testid="fractional-leave-name-input"
        />

        <LabelInput
          label={translate('fractionalLeave:form.description')}
          value={descriptionValue}
          onChange={handleDescriptionChange}
          required
          disabled={effectiveReadOnly || isSubmitting}
          color={errors.description && isSubmitted ? 'error' : 'default'}
          supportingText={
            errors.description?.message && isSubmitted
              ? translate(String(errors.description.message))
              : undefined
          }
          data-testid="fractional-leave-description-input"
        />

        <TimePicker
          label={translate('fractionalLeave:form.hours')}
          value={hoursValue}
          onChange={handleHoursChange}
          disabled={effectiveReadOnly || isSubmitting}
          defaultValue=""
          showSeconds
          color={errors.hours && isSubmitted ? 'error' : 'default'}
          supportingText={
          errors.hours?.message && isSubmitted
            ? translate(String(errors.hours.message))
            : undefined
        }
          data-testid="fractional-leave-hours-picker"
        />

        <Checkbox
          label={translate('fractionalLeave:form.deductible')}
          checked={deductibleValue}
          onChange={handleDeductibleChange}
          disabled={effectiveReadOnly || isSubmitting}
          data-testid="fractional-leave-deductible-checkbox"
        />
      </SidePanelFormLayout.Fields>

      {!effectiveReadOnly && (
        <SidePanelFormLayout.Footer>
          <PermissionGuard action="edit">
            <PrimaryButton
              type="submit"
              fullWidth
              isLoading={isSubmitting}
              disabled={!isReady || isSubmitting}
            >
              {translate('common:actions.save')}
            </PrimaryButton>
          </PermissionGuard>
        </SidePanelFormLayout.Footer>
      )}
    </SidePanelFormLayout>
  );
};

export default FractionalLeaveFormPanel;
